import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Patreon vs Devotion Fund: Which Is Better for Missionary Support in 2026?';
const DESCRIPTION =
    "An honest comparison from a competing founder: Patreon's real 13-16% all-in cost vs Devotion Fund's 5-7% all-in, and why the category mismatch matters more than the fee gap for missionary support raising.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover9.jpg',
            datePublished: '2026-07-23',
            dateModified: '2026-07-23',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/patreon-vs-devotion-fund',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How much does Patreon actually cost, all in?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Patreon's headline platform fee is 10% for creators who joined after August 2025 (legacy creators from before that date may still be on 5-8% tiers). Add payment processing (roughly 2.9% + $0.30 per pledge) and currency-conversion fees on international support, and the realistic all-in cost is 13-16%.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is Patreon or Devotion Fund cheaper for missionaries?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Devotion Fund is cheaper on raw fees at every gift size — 5-7% all-in (5% bank transfer, 7% card) versus Patreon's realistic 13-16%. At $5,000/month in support, that gap is roughly $3,600-$6,600 a year kept by the missionary instead of the platform.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Why do some missionaries use Patreon anyway?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Mostly momentum: they already had an audience there from content creation, or supporters are habituated to the brand. Patreon's tiered-membership tooling is genuinely mature. But it was built for creators selling access to content, not for partnership-style missionary support, and that category mismatch shows up in how the relationship feels to donors.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I move my support base off Patreon?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Run the numbers before moving — a mid-campaign switch has its own friction and some supporters may not follow. But if you're just starting to raise recurring support, or your Patreon fees are visibly eating into your budget, starting on a platform built for missionary partnership rather than content tiers is usually the better long-term call.",
                    },
                },
            ],
        },
    ],
};

export default function PatreonVsDevotionFund() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/patreon-vs-devotion-fund' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-patreon-vs-df-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover9.jpg" alt="A missionary recording a video update for supporters" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A handful of missionaries land on Patreon every year, usually by accident — it's the recurring-payments platform they already know from podcasters and YouTubers, so when they need monthly support, it's the first tool that comes to mind. I run a competing platform, so weigh what follows accordingly. But the honest version of this comparison is short: Patreon is the most expensive serious option for missionary support raising, and it wasn't built for this job in the first place.</p>

                    <h2>The short version</h2>

                    <p>Patreon is a <strong>membership</strong> platform — creators sell tiered access to content (early videos, exclusive posts, a Discord) and fans pledge monthly to unlock it. Devotion Fund is a <strong>recurring-support</strong> platform built specifically for missionaries and ministry workers raising ongoing monthly partnership, with no content-tier mechanic required.</p>

                    <p>The two can look similar from a distance — both take a recurring monthly pledge — but the category is different, and so is the price. Patreon's realistic all-in cost is 13-16%. Devotion Fund's is 5% on bank transfers or 7% on card, all in.</p>

                    <h2>What Patreon actually costs in 2026</h2>

                    <p>Patreon's headline platform fee is <strong>10%</strong> for any creator who joined after August 2025 (legacy creators from before that cutoff may still be grandfathered onto older 5-8% tiers). That number gets quoted on its own constantly, and it's misleading on its own, because it doesn't include:</p>
                    <ul>
                        <li><strong>Payment processing</strong> — roughly 2.9% + $0.30 per pledge, charged on top of the platform fee.</li>
                        <li><strong>Currency-conversion fees</strong> — if a supporter pledges in a currency different from your payout currency, Patreon adds a conversion charge, which matters for missionaries with an international supporter base.</li>
                        <li><strong>Payout fees</strong> in some regions, depending on how funds are withdrawn.</li>
                    </ul>

                    <p>Stack those together and the realistic all-in cost lands at <strong>13-16%</strong> of what your supporters pledge — the highest of any platform in our <a href="/resources/true-cost-missionary-fundraising-platforms-2026">full seven-platform cost comparison</a>. At $5,000/month in support, that's <strong>$650-$800 lost every single month</strong>, or roughly $7,800-$9,600 a year that never reaches the field.</p>

                    <h2>The math against Devotion Fund's 5-7%</h2>

                    <p>Devotion Fund charges a single all-in rate — 5% on bank transfers, 7% on card, processing absorbed, nothing added on top, no currency-conversion surprise. Run the same $5,000/month support base through both platforms:</p>

                    <ul>
                        <li><strong>Patreon (13-16% all-in):</strong> $650-$800/month lost, or $7,800-$9,600/year.</li>
                        <li><strong>Devotion Fund (5% bank transfer / 7% card):</strong> $250-$350/month, or $3,000-$4,200/year.</li>
                    </ul>

                    <p>The gap — roughly <strong>$3,600-$6,600 a year</strong> — is money that stays with the missionary under Devotion Fund instead of being split between platform fee, processor, and currency conversion. Unlike our comparisons against GiveSendGo or Continue to Give, there's no crossover point here to hedge on: Devotion Fund is cheaper than Patreon at every realistic gift size and support level.</p>

                    <h2>Why the category mismatch matters more than the fee gap</h2>

                    <p>Even setting the fees aside, Patreon's core mechanic is built for a different relationship than missionary support raising.</p>

                    <p><strong>Patreon sells access; missionary support is partnership.</strong> Patreon's whole design pattern is tiers — $5 gets you early posts, $15 gets you a Discord role, $50 gets you a shoutout. That's the right model for a podcaster or an artist. It's an awkward fit for a supporter who wants to partner in your ministry, not unlock content from you. Framing prayer partnership as a "membership tier" undersells what the relationship actually is.</p>

                    <p><strong>Retention is the real cost, and Patreon isn't built to help with it.</strong> Support doesn't usually lapse because of Patreon's fee — it lapses because supporters stop hearing from the missionary and quietly drift away. Devotion Fund bundles the newsletter and donor CRM into the same tool that holds the money, with an AI assist that turns a few bullet points into a real prayer-letter draft. Patreon has none of that built for a missions context — you're managing the relationship somewhere else entirely. (See <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">how to write a prayer letter people actually read</a> for the method, regardless of platform.)</p>

                    <p><strong>Missionary-specific onboarding.</strong> Devotion Fund assumes you're support-raised from day one — direct payouts, no requirement to form your own 501(c)(3), and setup built around a support page rather than a content library.</p>

                    <h2>Where Patreon is genuinely the better choice</h2>

                    <p>I'd point someone to Patreon without hesitation if:</p>
                    <ul>
                        <li><strong>You already have an active, habituated audience there</strong> from content creation, and the switching cost of moving them outweighs the fee gap in the short term.</li>
                        <li><strong>Your ministry actually involves ongoing content</strong> — teaching series, devotionals, behind-the-scenes video — where tiered access genuinely fits what you're offering.</li>
                        <li><strong>You want mature creator tooling</strong> — Patreon's membership and content-delivery features are a real strength if content is the product.</li>
                    </ul>

                    <h2>Where Devotion Fund is the better choice</h2>

                    <p>Choose us when:</p>
                    <ul>
                        <li><strong>You're raising missionary or ministry support</strong>, not selling access to content, and you want a platform built around partnership rather than tiers.</li>
                        <li><strong>The fee gap is real money to you</strong> — at most support levels it's thousands of dollars a year.</li>
                        <li><strong>Keeping supporters engaged is your actual bottleneck</strong> — the built-in updates and donor CRM exist specifically to solve that.</li>
                        <li><strong>You're starting from scratch</strong> and have no existing Patreon audience to protect by staying.</li>
                    </ul>

                    <p>If you've read all that and content-tier membership genuinely describes your ministry, Patreon may still be right for you — I'd rather you pick the tool that fits than pick mine. But if you're raising monthly partnership support and haven't already sunk years into a Patreon audience, <a href="/">start a page on Devotion Fund</a> — it takes about ten minutes, and I read every new signup personally.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How much does Patreon actually cost, all in?</strong><br />
                    Patreon's headline platform fee is 10% for creators who joined after August 2025 (legacy creators may still be on 5-8% tiers). Add payment processing (roughly 2.9% + $0.30 per pledge) and currency-conversion fees on international support, and the realistic all-in cost is 13-16%.</p>

                    <p><strong>Is Patreon or Devotion Fund cheaper for missionaries?</strong><br />
                    Devotion Fund is cheaper on raw fees at every gift size — 5-7% all-in (5% bank transfer, 7% card) versus Patreon's realistic 13-16%. At $5,000/month in support, that gap is roughly $3,600-$6,600 a year.</p>

                    <p><strong>Why do some missionaries use Patreon anyway?</strong><br />
                    Mostly momentum from an existing content audience, and mature membership tooling. But Patreon was built for creators selling content access, not missionary partnership support.</p>

                    <p><strong>Should I move my support base off Patreon?</strong><br />
                    Run the numbers first — switching has its own friction. But if you're just starting out, or your Patreon fees are visibly eating your budget, a platform built for missionary partnership is usually the better long-term call.</p>

                    <hr />
                    <p><small><em>Fees verified against Patreon's published Help Center pricing update (effective August 4, 2025) as of 2026-08-15. Devotion Fund's fee is all-in (card processing absorbed): 5% on bank transfers, 7% on card; Patreon's figures combine its 10% platform fee for post-August-2025 creators with typical payment-processing and currency-conversion costs. We update this post when either platform changes its pricing.</em></small></p>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund. We built this platform after watching missionary friends lose over a tenth of their support to Patreon fees they didn't realize they were paying — money that was meant for the field, not a content-subscription platform.</p>
                    <p><small><em>Last updated: 2026-08-15.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/patreon-vs-devotion-fund" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
